import { useCallback, useMemo, useState } from "react";
import adminApi from "../api/adminApi";
import { formatTimestamp, getDefaultAuditFilters, isSameData } from "../utils/adminUtils";

export default function useAudit(setBanner) {
  const defaultAuditFilters = useMemo(() => getDefaultAuditFilters(), []);
  const [auditFilters, setAuditFilters] = useState(defaultAuditFilters);

  const [registerAudit, setRegisterAudit] = useState([]);
  const [registerAuditLoading, setRegisterAuditLoading] = useState(false);

  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // The student id currently applied. We fetch the audit unfiltered by student
  // and narrow client-side so we can also catch records where the id is only in
  // the action text (e.g. student-creation rows whose student_id column is null).
  const [appliedStudentId, setAppliedStudentId] = useState("");

  const loadRegisterAudit = useCallback(async (silent = false, filtersOverride) => {
    const filters = filtersOverride ?? auditFilters;
    if (!silent) setRegisterAuditLoading(true);

    try {
      const data = await adminApi.getRegisterAudit(filters);
      const next = Array.isArray(data) ? data : [];
      setRegisterAudit((prev) => (isSameData(prev, next) ? prev : next));
    } catch (error) {
      setBanner({ type: "error", text: error.message || "Failed to load register audit" });
    } finally {
      if (!silent) setRegisterAuditLoading(false);
    }
  }, [auditFilters, setBanner]);

  const loadAudit = useCallback(async (silent = false, filtersOverride) => {
    const filters = filtersOverride ?? auditFilters;
    if (!silent) setAuditLoading(true);

    try {
      // Fetch unfiltered by student — the student narrowing happens client-side
      // (auditRows) so action-text-only matches are included.
      const data = await adminApi.getAudit({ ...filters, studentId: "" });
      const next = Array.isArray(data) ? data : [];
      setAudit((prev) => (isSameData(prev, next) ? prev : next));
    } catch (error) {
      setBanner({ type: "error", text: error.message || "Failed to load audit" });
    } finally {
      if (!silent) setAuditLoading(false);
    }
  }, [auditFilters, setBanner]);

  const registerAuditRows = useMemo(
    () =>
      registerAudit.map((row) => ({
        ...row,
        timestamp: formatTimestamp(row.timestamp),
      })),
    [registerAudit]
  );

  const auditRows = useMemo(() => {
    const id = String(appliedStudentId || "").trim();
    let rows = audit;
    if (id) {
      // Match the student_id column OR the id appearing as a whole number in the
      // action text (number-bounded so 13007 doesn't match 213007 / 130070).
      const inAction = new RegExp(`(^|\\D)${id}(\\D|$)`);
      rows = audit.filter(
        (row) =>
          String(row.student_id ?? "") === id ||
          inAction.test(String(row.action ?? ""))
      );
    }
    return rows.map((row) => ({
      ...row,
      timestamp: formatTimestamp(row.timestamp),
    }));
  }, [audit, appliedStudentId]);

  const applyAuditFilters = useCallback(() => {
    setAppliedStudentId(auditFilters.studentId || "");
    if (auditFilters.studentId) {
      setRegisterAudit([]);
      loadAudit(false, auditFilters);
    } else {
      loadRegisterAudit(false, auditFilters);
      loadAudit(false, auditFilters);
    }
  }, [loadRegisterAudit, loadAudit, auditFilters]);

  const clearAuditFilters = useCallback(() => {
    setAppliedStudentId("");
    setAuditFilters(defaultAuditFilters);
    setRegisterAudit([]);
    setAudit([]);
  }, [defaultAuditFilters]);

  return {
    auditFilters,
    setAuditFilters,
    defaultAuditFilters,
    registerAuditLoading,
    auditLoading,
    registerAuditRows,
    auditRows,
    loadRegisterAudit,
    loadAudit,
    applyAuditFilters,
    clearAuditFilters,
  };
}